import React, { useMemo, useRef, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import {
  Button,
  CenteredLoader,
  TitleWithIcon,
  useRequestBillingInformation,
} from 'components';
import {
  Participation,
  ParticipationStatus,
  StudyType,
  useAcceptParticipation,
  useApplication,
  useDeclineParticipation,
  useIsAdmin,
  useRecruitmentParticipantApplications,
  useSession,
  useUndoDeclineParticipation,
} from '@app/api';
import { ApiFormattedException } from 'store/modules/exceptions';

import { ApplicationDetails } from './ApplicationDetails';
import { ParticipationQuotaUpdate } from './ParticipationQuotaUpdate';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  buttons: {
    marginTop: theme.marginBase * 3,
    marginRight: -theme.marginBase,
  },
  button: {
    marginRight: theme.marginBase,
  },
  buttonVariant: {
    marginTop: theme.marginBase,
  },
  hr: {
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 0,
    },
    borderBottomWidth: 1,
  },
  otherApplicationTitle: {
    ...theme.fonts.h5,
  },
}));

interface Props {
  participation: Participation;
  className?: string;
  onAccept: () => any;
  onDecline: () => any;
  onBookingModalOpening?: () => any;
  onRequestMoveToOtherSession?: (id: string) => any;
}

const applicationStatus = [
  ParticipationStatus.APPLIED,
  ParticipationStatus.DECLINED,
  ParticipationStatus.ACCEPTED,
];

export const ScreeningFormResults = ({
  participation,
  className,
  onAccept,
  onDecline,
  onBookingModalOpening,
  onRequestMoveToOtherSession,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [showQuotaUpdate, setShowQuotaUpdate] = useState(false);
  const quotaUpdateRef = useRef<HTMLDivElement>(null);

  const { data: session } = useSession(participation.sessionId);

  const { mutate: mutateDecline } = useDeclineParticipation();
  const { mutateAsync: mutateAccept } = useAcceptParticipation();

  const { mutate: undoDecline } = useUndoDeclineParticipation();

  const { status } = participation;

  const { data: allApplications } = useRecruitmentParticipantApplications({
    recruitmentId: session?.recruitmentId,
    participantId: participation.participantId,
  });

  const otherApplications = useMemo(() => {
    return allApplications?.filter(
      (app) => app.id !== participation.mainApplicationId,
    );
  }, [allApplications, participation.mainApplicationId]);
  const isAdmin = useIsAdmin();
  const canUpdateFromOFQ =
    session?.studyType === StudyType.MODERATED &&
    status === ParticipationStatus.OUT_OF_QUOTA;

  const requestBillingInformation = useRequestBillingInformation();

  const decline = async () => {
    onDecline();
    mutateDecline(participation);
  };
  const restoreToApplied = async () => {
    undoDecline(participation);
  };

  const executeAccept = async () => {
    try {
      onAccept();
      await mutateAccept(participation);
    } catch (e) {
      if (
        e instanceof ApiFormattedException &&
        e.getCode() === 'INCOMPLETE_BILLING_INFO'
      ) {
        await requestBillingInformation();
        mutateAccept(participation);
      }
    }
  };

  const accept = async () => {
    if (canUpdateFromOFQ) {
      setShowQuotaUpdate(true);
      setTimeout(() => {
        quotaUpdateRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 10);
    } else {
      await executeAccept();
    }
  };

  const cancelQuotaIncrease = async () => {
    setShowQuotaUpdate(false);
  };

  if (!participation.mainApplicationId) {
    return null;
  }

  return (
    <>
      <div className={className}>
        <TitleWithIcon
          icon="ri-list-check-2"
          iconColor="green"
          title={t('sessions.details.monitoring.screening.title')}
        />
        <ApplicationDetails
          applicationId={participation.mainApplicationId}
          sessionId={participation.sessionId}
        />

        <div className={classes.buttons}>
          {(status === ParticipationStatus.APPLIED || canUpdateFromOFQ) && (
            <Button
              color="green"
              text={t('sessions.details.monitoring.screening.validate')}
              small
              className={classes.button}
              onClick={accept}
            />
          )}
          {(status === ParticipationStatus.APPLIED || canUpdateFromOFQ) && (
            <Button
              color="red"
              text={t('sessions.details.monitoring.screening.decline')}
              small
              className={classes.button}
              onClick={decline}
            />
          )}
          {status === ParticipationStatus.DECLINED && (
            <Button
              color="blue"
              text={t('sessions.details.monitoring.screening.restoreToApplied')}
              small
              className={classes.button}
              onClick={restoreToApplied}
            />
          )}
          {!session ||
            (session.studyType === StudyType.MODERATED &&
              (status === ParticipationStatus.ACCEPTED ||
                status === ParticipationStatus.APPLIED) && (
                <Button
                  color="ink"
                  variant="muted"
                  text={t('sessions.details.monitoring.screening.booking')}
                  small
                  className={classes.buttonVariant}
                  onClick={onBookingModalOpening}
                />
              ))}
          {applicationStatus.includes(status) &&
            !!onRequestMoveToOtherSession && (
              <Button
                color="purple"
                text={'Déplacer vers une autre session'}
                small
                className={classes.buttonVariant}
                onClick={() => onRequestMoveToOtherSession(participation.id)}
              />
            )}
        </div>
      </div>

      {showQuotaUpdate && (
        <div ref={quotaUpdateRef}>
          <ParticipationQuotaUpdate
            participation={participation}
            recruitmentId={session?.recruitmentId}
            onAccept={executeAccept}
            onCancel={cancelQuotaIncrease}
          />
        </div>
      )}

      {isAdmin && !!otherApplications?.length && (
        <div className={className}>
          <TitleWithIcon
            icon="ri-list-check-2"
            iconColor="purple"
            title={t('sessions.details.monitoring.screening.otherTitle')}
          />
          {otherApplications?.map((application) => (
            <>
              <div className={classes.otherApplicationTitle}>
                Application {application.status} - {application.id}
              </div>
              <ApplicationDetails
                applicationId={application.id}
                sessionId={participation.sessionId}
              />
              <hr className={classes.hr} />
            </>
          ))}
        </div>
      )}
    </>
  );
};
